import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useTranslation } from 'next-i18next';
import cx from 'classnames';

const useStyles = makeStyles(theme => ({
  root: {
    padding: '80px 0 40px 0',
    backgroundColor: '#fff',
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  name: {
    display: 'flex',
    fontSize: ({ language }) => (language === 'cn' ? 24 : 20),
    padding: '0 0 0 24px',
  },
  planValue: {
    display: 'flex',
    fontSize: ({ language }) => (language === 'cn' ? 20 : 16),
    justifyContent: 'center',
  },
  tableCellHead: {
    fontSize: ({ language }) => (language === 'cn' ? 24 : 20),
    color: '#fff',
    backgroundColor: '#828B9D',
    padding: '32px 24px',
    whiteSpace: 'nowrap',
  },
  oddRow: {
    backgroundColor: '#F7F7F8',
  },
  basicPlan: {
    backgroundColor: '#00A7E8',
  },
  storePlan: {
    backgroundColor: '#076BD1',
  },
  proPlan: {
    backgroundColor: '#0CB392',
  },
  companyPlan: {
    backgroundColor: '#8070D7',
  },
  tableCellBody: {
    backgroundColor: '#F7F7F8',
    borderBottom: 0,
  },
  tableCellBodyOdd: {
    backgroundColor: '#f2f2f2',
    borderBottom: 0,
  },
  registerButton: {
    backgroundColor: '#076BD1',
    borderRadius: 40,
    height: 38,
    display: 'inline-block',
    [theme.breakpoints.up('md')]: {
      height: 50,
    },
  },
  registerLabel: {
    color: '#fff',
    padding: '12px 36px',
    fontSize: 16,
    lineHeight: '19px',
    position: 'relative',
    top: 10,
    whiteSpace: 'nowrap',
    [theme.breakpoints.up('md')]: {
      top: 12,
      fontSize: 20,
      lineHeight: '24px',
    },
  },
  tableRoot: {
    borderRadius: 10,
    overflow: 'hidden',
  },
  tableCell: {
    borderBottom: 0,
  },
}));

function createData(name, livestream, store, pro, company, customise) {
  return { name, livestream, store, pro, company, customise };
}

const ImageTick = () => <img src="/static/images/tick.svg" />;
const ImageCross = () => <img src="/static/images/cross.svg" />;

const PricingTable = () => {
  const { t, i18n } = useTranslation('common');
  const classes = useStyles({ language: i18n.language });
  const rows = [
    createData(
      t('auto-order-capture'),
      <ImageTick />,
      <ImageCross />,
      <ImageTick />,
      <ImageTick />
    ),
    createData(
      t('manual-order-capture'),
      <ImageTick />,
      <ImageCross />,
      <ImageTick />,
      <ImageTick />
    ),
    createData(
      t('facebook-notification'),
      <ImageTick />,
      <ImageTick />,
      <ImageTick />,
      <ImageTick />
    ),
    createData(
      t('split-combine-order'),
      <ImageTick />,
      <ImageTick />,
      <ImageTick />,
      <ImageTick />
    ),
    createData(
      t('auto-capture-order'),
      <ImageTick />,
      <ImageCross />,
      <ImageTick />,
      <ImageTick />
    ),
    createData(
      t('customer-categorization-management'),
      <ImageTick />,
      <ImageTick />,
      <ImageTick />,
      <ImageTick />
    ),
    createData(t('feature-6'), <ImageTick />, <ImageCross />, <ImageTick />, <ImageTick />),
    createData(t('feature-9'), <ImageTick />, <ImageTick />, <ImageTick />, <ImageTick />),
    createData(t('subfeature-5'), <ImageTick />, <ImageTick />, <ImageTick />, <ImageTick />),
    createData(
      t('sales-data-reports'),
      <ImageTick />,
      <ImageTick />,
      <ImageTick />,
      <ImageTick />,

      <a
        href="https://api.whatsapp.com/send/?phone=60162433083&text=你好，想知道有关抓单系统的详情。"
        className={classes.registerButton}
      >
        <span className={classes.registerLabel}>{t('contact-us')}</span>
      </a>
    ),
    createData(t('batch-print'), 10, 10, 50, t('unlimited')),
    createData(t('lucky-draw'), <ImageCross />, <ImageCross />, <ImageTick />, <ImageTick />),
    createData(t('feature-7'), <ImageCross />, <ImageTick />, <ImageTick />, <ImageTick />),
    createData(t('feature-2'), <ImageCross />, <ImageTick />, <ImageTick />, <ImageTick />),
    createData(t('feature-3'), <ImageCross />, 1, 1, 3),
    createData(t('feature-10'), <ImageCross />, <ImageCross />, <ImageCross />, 3),
    createData(t('multiple-account-management'), 1, 1, 3, 5),
    createData(
      t('payment-receive-notification'),
      <ImageTick />,
      <ImageTick />,
      <ImageTick />,
      <ImageTick />
    ),
    createData('Offline Banking', <ImageTick />, <ImageTick />, <ImageTick />, <ImageTick />),
    createData('Online Banking FPX', <ImageTick />, <ImageTick />, <ImageTick />, <ImageTick />),
    createData('E-Wallet', <ImageTick />, <ImageTick />, <ImageTick />, <ImageTick />),
  ];
  return (
    <div className={classes.root}>
      <div className={['container', classes.contentContainer].join(' ')}>
        <Table classes={{ root: classes.tableRoot }} sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell classes={{ head: classes.tableCellHead }}>{t('features')}</TableCell>
              <TableCell
                align="center"
                classes={{ head: [classes.tableCellHead, classes.basicPlan].join(' ') }}
              >
                {t('basic-plan')}
              </TableCell>
              <TableCell
                align="center"
                classes={{ head: [classes.tableCellHead, classes.storePlan].join(' ') }}
              >
                {t('store-plan')}
              </TableCell>
              <TableCell
                align="center"
                classes={{ head: [classes.tableCellHead, classes.proPlan].join(' ') }}
              >
                {t('pro-plan')}
              </TableCell>
              <TableCell
                align="center"
                classes={{ head: [classes.tableCellHead, classes.companyPlan].join(' ') }}
              >
                {t('company-plan')}
              </TableCell>
              <TableCell align="center" classes={{ head: classes.tableCellHead }}>
                {t('customise-plan')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                className={cx('', { [classes.oddRow]: i % 2 === 0 })}
                key={row.name}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell className={classes.tableCell} component="th" scope="row">
                  <span className={classes.name}>{row.name}</span>
                </TableCell>
                <TableCell
                  align="center"
                  classes={{ body: i % 2 ? classes.tableCellBody : classes.tableCellBodyOdd }}
                >
                  <span className={classes.planValue}>{row.livestream}</span>
                </TableCell>
                <TableCell className={classes.tableCell} align="center">
                  <span className={classes.planValue}>{row.store}</span>
                </TableCell>
                <TableCell className={classes.tableCell} align="center">
                  <span className={classes.planValue}>{row.pro}</span>
                </TableCell>
                <TableCell className={classes.tableCell} align="center">
                  <span className={classes.planValue}>{row.company}</span>
                </TableCell>
                <TableCell
                  align="center"
                  classes={{ body: i % 2 ? classes.tableCellBody : classes.tableCellBodyOdd }}
                >
                  <span className={classes.planValue}>{row.customise}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default PricingTable;
